import AvailabilityNotice from '../../components/AvailabilityNotice/AvailabilityNotice';
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NamedLink, AuthPromptModal } from '../../components';
import OfferBadge from '../../components/OfferBadge/OfferBadge';
import { OfferTypeFilter } from '../../components/OfferSearchFilters/OfferSearchFilters';
import { getOfferType, priceUnit } from '../../util/offerType';
import { commitmentPriceLabel } from '../../util/commitmentPricing';
import { formatMoney } from '../../util/currency';
import { useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { getAdvertiserProfile } from '../../util/advertiserProfile';
import { advertiserCoverUrl, getAdvertiserCoverUrl } from '../../util/advertiserBranding';
import { toggleFavorite, selectIsFavorite } from '../../ducks/favorites.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { trackListingClick } from '../../util/listingAnalytics';
import NosAnnoncesMap from '../NosAnnonces2Page/NosAnnoncesMap';
import css from './AdvertiserShowcase.module.css';

const Arrow = () => <span aria-hidden="true">→</span>;
const Heart = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ fill: filled ? 'currentColor' : 'none' }}
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
  </svg>
);
export const listingPhoto = listing => {
  const variants = listing?.images?.[0]?.attributes?.variants || {};
  return (
    variants['listing-card-2x']?.url ||
    variants['listing-card']?.url ||
    variants['scaled-medium']?.url ||
    variants['scaled-small']?.url ||
    null
  );
};
const cityOf = listing => {
  const location = listing.attributes?.publicData?.location || {};
  return location.streetCity || location.city || location.address?.split(',')[0] || '';
};
const initials = name =>
  name
    .split(/\s+/)
    .map(word => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
const ExternalLink = ({ href, children, ...props }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

function Listing({ listing, category, onAuthRequired }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const id = listing.id.uuid;
  const favorite = useSelector(state => selectIsFavorite(state, id));
  const user = useSelector(state => state.user.currentUser);
  const { title, price, publicData = {} } = listing.attributes;
  const photo = listingPhoto(listing);
  const params = { id, slug: createSlug(title) };
  return (
    <article className={css.listing}>
      <div className={css.listingMedia}>
        <NamedLink
          name="ListingPage"
          params={params}
          onClick={() => trackListingClick(id, 'profile')}
        >
          {photo ? (
            <img src={photo} alt={title} loading="lazy" />
          ) : (
            <span className={css.photoPlaceholder}>Photo à venir</span>
          )}
        </NamedLink>
        <OfferBadge className={css.offerBadge} publicData={publicData} />
        <button
          type="button"
          className={css.favorite}
          aria-pressed={favorite}
          aria-label={favorite ? `Retirer ${title} des favoris` : `Ajouter ${title} aux favoris`}
          onClick={() => (user ? dispatch(toggleFavorite(id)) : onAuthRequired())}
        >
          <Heart filled={favorite} />
        </button>
        <AvailabilityNotice listing={listing} compact overlay />
      </div>
      <div className={css.listingBody}>
        <p className={css.category}>{category}</p>
        <h3>
          <NamedLink name="ListingPage" params={params}>
            {title}
          </NamedLink>
        </h3>
        {cityOf(listing) && <p className={css.muted}>{cityOf(listing)}</p>}
        <p className={css.specs}>
          {publicData.areaSize && <span>{publicData.areaSize} m²</span>}
          {publicData.seatedStations && <span>{publicData.seatedStations} postes</span>}
        </p>
        {price && (
          <p className={css.price}>
            <strong>{commitmentPriceLabel(publicData) ? 'À partir de ' : ''}{formatMoney(intl, price)}</strong>
            <span>{priceUnit(publicData)}{commitmentPriceLabel(publicData) ? ` · ${commitmentPriceLabel(publicData)}` : ''}</span>
          </p>
        )}
        <NamedLink name="ListingPage" params={params} className={css.textLink}>
          Découvrir cet espace <Arrow />
        </NamedLink>
      </div>
    </article>
  );
}

export default function AdvertiserShowcase({
  user,
  listings = [],
  listingTypes = [],
  isOwner = false,
  preview = false,
  queryListingsError,
  loading = false,
}) {
  const dispatch = useDispatch();
  const [offerType, setOfferType] = useState('all');
  const [city, setCity] = useState('all');
  const [brokerIndex, setBrokerIndex] = useState(null);
  const [limit, setLimit] = useState(12);
  const [activeMapId, setActiveMapId] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const sections = useRef({});
  const profile = user?.attributes?.profile || {};
  const settings = getAdvertiserProfile(profile.publicData);
  const name = profile.displayName || 'Annonceur Cobuco';
  const logoVariants = user?.profileImage?.attributes?.variants || {};
  const logo = logoVariants['square-small2x']?.url || logoVariants['square-small']?.url;
  const cover = getAdvertiserCoverUrl(user) || listings.map(listingPhoto).find(Boolean);
  const cities = [...new Set(listings.map(cityOf).filter(Boolean))];
  const team = settings.team.filter(member => member.name);
  const strengths = settings.strengths.filter(strength => strength.title || strength.description);
  const contactAvailable =
    settings.publicEmail ||
    settings.publicPhone ||
    settings.websiteLink ||
    settings.publicAddress ||
    settings.openingHours;
  const hasAbout = profile.bio || strengths.length > 0;
  const hasGallery = settings.showGallery && settings.gallery.length > 0;
  const hasTeam = settings.showTeam && team.length > 0;
  const hasMap = !preview && settings.showMap && listings.some(l => l.attributes?.geolocation);
  const filtered = listings.filter(
    listing =>
      (offerType === 'all' || getOfferType(listing.attributes.publicData) === offerType) &&
      (city === 'all' || cityOf(listing) === city) &&
      (brokerIndex === null || team[brokerIndex]?.listingIds.includes(listing.id.uuid))
  );
  const change = setter => value => {
    setter(value);
    setLimit(12);
  };
  const scrollTo = section =>
    sections.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const socialLinks = [
    ['LinkedIn', settings.linkedin],
    ['Instagram', settings.instagram],
    ['Facebook', settings.facebook],
  ].filter(([, url]) => url);

  return (
    <div
      className={css.root}
      onClickCapture={
        preview
          ? event => {
              if (event.target.closest('a')) event.preventDefault();
            }
          : undefined
      }
    >
      {!preview && (
        <nav className={css.breadcrumb} aria-label="Fil d’Ariane">
          <NamedLink name="LandingPage">Accueil</NamedLink>
          <span>/</span>
          <NamedLink name="AdvertisersPage">Nos annonceurs</NamedLink>
          <span>/</span>
          <span aria-current="page">{name}</span>
        </nav>
      )}
      <section className={css.hero}>
        <div className={css.cover}>
          {cover && (
            <img
              src={cover}
              alt=""
              style={{ objectPosition: `center ${settings.coverPosition}` }}
            />
          )}
        </div>
        <div className={css.identity}>
          <div className={css.logo}>
            {logo ? <img src={logo} alt={`Logo de ${name}`} /> : <span>{initials(name)}</span>}
          </div>
          <div className={css.identityText}>
            <p className={css.eyebrow}>VOTRE ANNONCEUR SUR COBUCO</p>
            <h1>{name}</h1>
            {settings.tagline && <p className={css.tagline}>{settings.tagline}</p>}
            <div className={css.meta}>
              {!preview && !loading && !queryListingsError && (
                <span>
                  {listings.length} annonce{listings.length !== 1 ? 's' : ''} publiée
                  {listings.length !== 1 ? 's' : ''}
                </span>
              )}
              {settings.yearCreated && <span>Depuis {settings.yearCreated}</span>}
            </div>
          </div>
          <div className={css.heroActions}>
            {contactAvailable && (
              <button
                type="button"
                className={css.primaryButton}
                onClick={() => scrollTo('contact')}
              >
                Prendre contact <Arrow />
              </button>
            )}
            {isOwner && !preview && (
              <NamedLink name="ProfileSettingsPage" className={css.editLink}>
                Personnaliser ma vitrine
              </NamedLink>
            )}
          </div>
        </div>
      </section>
      <nav className={css.navigation} aria-label="Sections de la vitrine">
        {[
          [hasAbout, 'about', 'Présentation'],
          [hasGallery, 'gallery', 'En images'],
          [true, 'listings', 'Nos espaces'],
          [hasTeam, 'team', 'Notre équipe'],
          [hasMap, 'map', 'Localisations'],
          [contactAvailable, 'contact', 'Contact'],
        ]
          .filter(([visible]) => visible)
          .map(([, id, label]) => (
            <button key={id} type="button" onClick={() => scrollTo(id)}>
              {label}
            </button>
          ))}
      </nav>
      <div className={css.columns}>
        <div className={css.mainColumn}>
          {hasAbout && (
            <section
              className={css.section}
              ref={node => {
                sections.current.about = node;
              }}
            >
              <p className={css.eyebrow}>FAISONS CONNAISSANCE</p>
              <h2>{settings.aboutTitle || `À propos de ${name}`}</h2>
              {profile.bio && <p className={css.bio}>{profile.bio}</p>}
              {strengths.length > 0 && (
                <div className={css.strengths}>
                  {strengths.map((item, index) => (
                    <div key={index}>
                      <span className={css.strengthNumber}>0{index + 1}</span>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
          {hasGallery && (
            <section
              className={css.section}
              ref={node => {
                sections.current.gallery = node;
              }}
            >
              <p className={css.eyebrow}>NOTRE UNIVERS</p>
              <h2>En images</h2>
              <div className={css.gallery}>
                {settings.gallery.map((photo, index) => (
                  <figure key={photo.id}>
                    <img
                      src={advertiserCoverUrl(photo.id)}
                      alt={photo.caption || `${name} — photo ${index + 1}`}
                      loading="lazy"
                    />
                    {photo.caption && <figcaption>{photo.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </section>
          )}
          <section
            className={css.section}
            ref={node => {
              sections.current.listings = node;
            }}
          >
            <p className={css.eyebrow}>LES ANNONCES DE {name.toUpperCase()}</p>
            <h2>{settings.portfolioTitle || 'Trouvez votre prochain espace'}</h2>
            {preview ? (
              <p className={css.empty}>Vos annonces publiées apparaissent automatiquement ici.</p>
            ) : (
              <>
                <div className={css.filters}>
                  <OfferTypeFilter
                    compact
                    value={offerType}
                    onChange={({ offerType }) => change(setOfferType)(offerType || 'all')}
                  />
                  {cities.length > 1 && (
                    <label>
                      Localisation
                      <select value={city} onChange={event => change(setCity)(event.target.value)}>
                        <option value="all">Toutes les localisations</option>
                        {cities.map(value => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                {brokerIndex !== null && (
                  <button
                    type="button"
                    className={css.clearFilter}
                    onClick={() => change(setBrokerIndex)(null)}
                  >
                    Annonces de {team[brokerIndex]?.name} · Retirer le filtre ×
                  </button>
                )}
                {!loading && !queryListingsError && (
                  <p className={css.resultCount} aria-live="polite">
                    {filtered.length} annonce{filtered.length !== 1 ? 's' : ''}
                  </p>
                )}
                {loading ? (
                  <p role="status" className={css.empty}>
                    Chargement des annonces…
                  </p>
                ) : queryListingsError ? (
                  <p role="alert" className={css.empty}>
                    Les annonces n’ont pas pu être chargées. Réessayez en actualisant la page.
                  </p>
                ) : filtered.length ? (
                  <div className={css.listingGrid}>
                    {filtered.slice(0, limit).map(listing => (
                      <Listing
                        key={listing.id.uuid}
                        listing={listing}
                        category={
                          listingTypes.find(
                            type => type.listingType === listing.attributes.publicData?.listingType
                          )?.label || 'Espace professionnel'
                        }
                        onAuthRequired={() => setAuthOpen(true)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className={css.empty}>
                    {listings.length
                      ? 'Aucune annonce ne correspond à ces critères.'
                      : 'Aucune annonce publiée pour le moment.'}
                  </p>
                )}
                {filtered.length > limit && (
                  <button
                    type="button"
                    className={css.secondaryButton}
                    onClick={() => setLimit(limit + 12)}
                  >
                    Voir plus d’annonces <Arrow />
                  </button>
                )}
              </>
            )}
          </section>
          {hasTeam && (
            <section
              className={css.section}
              ref={node => {
                sections.current.team = node;
              }}
            >
              <p className={css.eyebrow}>VOS INTERLOCUTEURS</p>
              <h2>{settings.teamTitle || 'Une équipe à votre écoute'}</h2>
              <div className={css.team}>
                {team.map((member, index) => (
                  <article key={index} className={css.teamCard}>
                    <div className={css.teamIdentity}>
                      <div className={css.teamPhoto}>
                        {member.photoId || member.avatarUrl ? (
                          <img
                            src={advertiserCoverUrl(member.photoId) || member.avatarUrl}
                            alt={member.name}
                            loading="lazy"
                          />
                        ) : (
                          <span>{initials(member.name)}</span>
                        )}
                      </div>
                      <div>
                        <h3>{member.name}</h3>
                        {member.role && <p>{member.role}</p>}
                      </div>
                    </div>
                    {member.bio && <p className={css.memberBio}>{member.bio}</p>}
                    <div className={css.teamLinks}>
                      {member.email && (
                        <a href={`mailto:${member.email}`}>Écrire à {member.name}</a>
                      )}
                      {member.phone && (
                        <a href={`tel:${member.phone.replace(/[^+\d]/g, '')}`}>{member.phone}</a>
                      )}
                      {member.linkedin && (
                        <ExternalLink href={member.linkedin}>
                          LinkedIn <Arrow />
                        </ExternalLink>
                      )}
                      {member.listingIds.length > 0 && !preview && (
                        <button
                          type="button"
                          onClick={() => {
                            change(setBrokerIndex)(index);
                            scrollTo('listings');
                          }}
                        >
                          Voir ses annonces <Arrow />
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
          {hasMap && (
            <section
              className={css.section}
              ref={node => {
                sections.current.map = node;
              }}
            >
              <p className={css.eyebrow}>À LA BONNE ADRESSE</p>
              <h2>Retrouvez nos espaces</h2>
              <div className={css.map}>
                <NosAnnoncesMap
                  activeId={activeMapId}
                  onMarkerHover={setActiveMapId}
                  listings={filtered.map(listing => ({
                    id: listing.id.uuid,
                    availabilityData: listing.attributes.publicData,
                    slug: createSlug(listing.attributes.title),
                    title: listing.attributes.title,
                    geo: listing.attributes.geolocation,
                    price: listing.attributes.price,
                    offerType: getOfferType(listing.attributes.publicData),
                    unit: priceUnit(listing.attributes.publicData),
                    streetCity: cityOf(listing),
                    imageUrl: listingPhoto(listing),
                  }))}
                />
              </div>
            </section>
          )}
        </div>
        <aside
          className={css.sidebar}
          ref={node => {
            sections.current.contact = node;
          }}
        >
          <div className={css.contactCard}>
            <p className={css.eyebrow}>ÉCHANGEONS</p>
            <h2>{settings.contactTitle || 'Parlons de votre projet'}</h2>
            <p className={css.contactIntro}>{settings.contactName || name}</p>
            {settings.publicPhone && (
              <a
                className={css.contactLink}
                href={`tel:${settings.publicPhone.replace(/[^+\d]/g, '')}`}
              >
                <small>Téléphone</small>
                <span>{settings.publicPhone}</span>
              </a>
            )}
            {settings.publicEmail && (
              <a className={css.contactLink} href={`mailto:${settings.publicEmail}`}>
                <small>E-mail</small>
                <span>{settings.publicEmail}</span>
              </a>
            )}
            {settings.websiteLink && (
              <ExternalLink className={css.contactLink} href={settings.websiteLink}>
                <small>Site internet</small>
                <span>
                  {new URL(settings.websiteLink).hostname} <Arrow />
                </span>
              </ExternalLink>
            )}
            {settings.publicAddress && (
              <div className={css.contactInfo}>
                <small>Adresse</small>
                <p>{settings.publicAddress}</p>
              </div>
            )}
            {settings.openingHours && (
              <div className={css.contactInfo}>
                <small>Horaires</small>
                <p>{settings.openingHours}</p>
              </div>
            )}
            {!contactAvailable && (
              <p className={css.contactInfo}>
                Pour contacter cet annonceur, consultez l’une de ses annonces.
              </p>
            )}
            {socialLinks.length > 0 && (
              <div className={css.socialLinks}>
                {socialLinks.map(([label, href]) => (
                  <ExternalLink href={href} key={label}>
                    {label} <Arrow />
                  </ExternalLink>
                ))}
              </div>
            )}
            {isOwner && !preview && (
              <NamedLink name="ProfileSettingsPage" className={css.textLink}>
                Modifier mes informations <Arrow />
              </NamedLink>
            )}
          </div>
        </aside>
      </div>
      {!preview && (
        <AuthPromptModal
          isOpen={authOpen}
          onClose={() => setAuthOpen(false)}
          onManageDisableScrolling={(id, disabled) =>
            dispatch(manageDisableScrolling(id, disabled))
          }
        />
      )}
    </div>
  );
}
